import { MILES_TO_METERS } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import MapBox from '../../map/MapBox.ts';
import { createCircleCoordinates } from './createCircleCoordinates.ts';

const CIRCLE_POINTS = 64;
const SOURCE_ID = 'proximity-requirement-source';
const FILL_LAYER_ID = 'proximity-requirement-fill';
const OUTLINE_LAYER_ID = 'proximity-requirement-outline';

export function useProximityRequirementCircle({
  mapRef,
  subjectLat,
  subjectLng,
  proximityRequirementMiles,
  mapLoaded,
}: {
  mapRef: React.RefObject<MapBox>;
  subjectLat: number;
  subjectLng: number;
  proximityRequirementMiles: number | undefined;
  mapLoaded: boolean;
}) {
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!mapRef.current || !mapLoaded || !map) return;

    const source = map.getSource(SOURCE_ID);
    if (!proximityRequirementMiles || proximityRequirementMiles <= 0) {
      if (source && source.type === 'geojson') {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
      return;
    }

    const radiusInMeters = proximityRequirementMiles * MILES_TO_METERS;
    const circleCoordinates = createCircleCoordinates(
      { latitude: subjectLat, longitude: subjectLng },
      radiusInMeters,
      CIRCLE_POINTS,
    );

    const data = {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          geometry: { type: 'Polygon' as const, coordinates: [circleCoordinates] },
          properties: {},
        },
      ],
    };

    if (source && source.type === 'geojson') {
      source.setData(data);
    } else {
      map.addSource(SOURCE_ID, { type: 'geojson', data });
    }

    if (!map.getLayer(FILL_LAYER_ID)) {
      map.addLayer({
        id: FILL_LAYER_ID,
        type: 'fill',
        source: SOURCE_ID,
        layout: {},
        paint: {
          'fill-color': '#019E73',
          'fill-opacity': 0.06,
        },
      });
    }

    if (!map.getLayer(OUTLINE_LAYER_ID)) {
      map.addLayer({
        id: OUTLINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#019E73',
          'line-width': 2,
          'line-dasharray': [2, 2],
        },
      });
    }
  }, [mapLoaded, mapRef, proximityRequirementMiles, subjectLat, subjectLng]);
}
